import ServiceLocator from '@tolkam/lib-service-locator';
import { Container, ReactDOMFactory, Renderer } from '@tolkam/lib-react-renderer';
import Configuration from '@tolkam/lib-config';
import MatchMedia from '@tolkam/lib-match-media';
import Http, {IRequestConfig} from '@tolkam/lib-http';
import { StaticProvider, Translator } from '@tolkam/lib-translator';

class Locator extends ServiceLocator {

    /**
     * App configuration
     */
    public get config(): Configuration {
        const debug = process.env.NODE_ENV !== 'production';
        const config = new Configuration({
            debug: debug,
        }, {
            envNamespace: 'tkmApp',
            environment: window,
            strictEnv: debug,
        });
        config.commit();

        return config;
    }

    /**
     * React elements renderer
     */
    public get elementRenderer(): Renderer {
        return this.cache('elementRenderer', () => {
            const container = new Container(new ReactDOMFactory({
                noRoot: true,
                allowMultipleChildren: false,
            }));

            return new Renderer(container);
        })
    }

    /**
     * Match media listener
     */
    public get matchMedia(): MatchMedia {
        return this.cache('matchMedia', () => {
            return new MatchMedia({
                mouse: '(pointer: fine)',
                s: 'all',
                m: '(min-width:800px)',
                l: '(min-width:1024px)',
                xl: '(min-width:1366px)',
                sOnly: 'all and (max-width:799px)',
                mOnly: '(min-width:800px) and (max-width:1023px)',
                lOnly: '(min-width:1024px) and (min-width:1365px)',
            });
        });
    }

    /**
     * API HTTP service
     */
    public get apiService(): Http {
        return this.cache('apiService', ({ config }) => {

            const httpConfig: IRequestConfig = {
                baseURL: config.getEnv('runtime.apiBase') as string,
                timeout: 60 * 1e3,
                headers: {
                    Accept: 'application/vnd.tolkam.api+json',
                },
                withCredentials: true,
                validateStatus: (code) => code < 305 || code === 422,
            };

            return new Http(httpConfig);
        });
    }

    public get translator(): Translator {
        return this.cache('translator', ({config}) => {
            const debug = config.get('debug');
            const currentLanguage = config.getEnv('runtime.language');

            const translator = new Translator({
                fallbackToCode: !debug,
            });

            const staticProvider = new StaticProvider({
                strict: debug,
            });

            // runtime messages from backend
            const externalMessages = config.getEnv('runtime.messages', {});
            staticProvider.add(currentLanguage, externalMessages);

            return translator
                .addProvider(staticProvider)
                .useLanguage(currentLanguage);
        });
    }
}

interface ILocator extends Locator {}

export default new Locator();
export { ILocator };
